import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { menuItems, profileItems, guestItems, type MenuItem } from '@/config/navigation';
import { shouldShowMenuItem, getUserBadge } from '@/utils/navigation';

const Navbar = () => {
  const homePath = import.meta.env.VITE_APP_HOME || '/';
  const { user, isLoading, logout } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  // const [isAuth, setIsAuth] = useState(!!user?.isAuthenticated || tokenManager.isAuthenticated());
  const [isAuth, setIsAuth] = useState(!!user?.isAuthenticated);
  const [isLoad, setIsLoad] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar || user?.profile?.avatar || null);
  const [roleBadge, setRoleBadge] = useState(user?.role ? getUserBadge(user?.role) : null);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleProfileMenu = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  // Function to check if a link is active
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  // Handle menu item click (for logout and other actions)
  const handleMenuItemClick = (item: MenuItem) => {
    if (item.action === 'logout') {
      handleLogout();
    }
    // Close mobile menu if open
    setIsOpen(false);
    // Close profile dropdown if open
    setIsProfileOpen(false);
  };

  // Logout function
  const handleLogout = async () => {
    try {
      setIsLoad(true);
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoad(false);
    }
  };

  // Filter menu items based on user role and auth status
  const filteredMenuItems = menuItems.filter(item => 
    shouldShowMenuItem(item, user, isAuth)
  );

  const filteredProfileItems = profileItems.filter(item =>
    shouldShowMenuItem(item, user, isAuth)
  );

  const filteredGuestItems = guestItems.filter(item =>
    shouldShowMenuItem(item, user, isAuth)
  );

  useEffect(() => {
    setIsAuth(!!user?.isAuthenticated);
    setRoleBadge(user?.role ? getUserBadge(user?.role) : null);
  }, [user?.isAuthenticated, isLoading, user?.role]); 

  useEffect(() => {
    setAvatarUrl(user?.avatar || user?.profile?.avatar || null);
  }, [user?.avatar, user?.profile?.avatar]); 

  let navbarLogoSrc = `/navbar-logo-latest-white.png`;
  let navbarLogoAlt = `SKILLSTURE`;
  let navbarLogoCls = `h-10`;

  if (roleBadge) {
    navbarLogoSrc = `/navbar-logo-plus-white.png`;
    navbarLogoAlt = `${navbarLogoAlt} ${roleBadge}`;
    navbarLogoCls = `h-16 w-auto object-cover`;
  }

  return (
    <nav className="bg-transparent bg-opacity-90 backdrop-blur-sm shadow-sm rounded-full sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between h-16 items-center">
          {/* Logo and Main Navigation */}
          <div className="flex items-center space-x-8">
            {/* Logo with badge */}
            <div className="flex-shrink-0 flex items-center">
              <a href={homePath} className="flex items-center">
                <div className="relative">
                {/* <span className="text-2xl font-bold text-gray-900">
                  Skillsture
                </span> */}
                  <img
                    src={navbarLogoSrc}
                    alt={`${navbarLogoAlt} Navbar Logo`}
                    className={navbarLogoCls}
                    onError={(e) => e.currentTarget.src = '/navbar-logo.png'}
                  />
                  {/* {roleBadge && (
                    <span className="absolute top-2 -right-12 bg-gradient-to-br from-white to-orange-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      {roleBadge}
                    </span>
                  )} */}
                </div>
              </a>
            </div>

            {/* Desktop menu - rendered dynamically */}
            <div className={`hidden md:flex items-center ${roleBadge ? 'pl-4' : ''}`}>
              {filteredMenuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  target={item.external ? '_blank' : '_self'}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? "text-orange-600 border-b-2 border-orange-600"
                      : "text-white hover:text-orange-600"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Right side - Search, Notifications, Profile/Auth */}
          <div className="flex items-center space-x-2">
            {/* Search */}
            {/* {isAuth && (
              <div className="hidden md:block relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 border bg-transparent border-gray-300 rounded-full focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                />
              </div>
            )} */}

            {/* Notifications */}
            {/* <button className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-2 h-2"></span>
            </button> */}
            
            {/* Spiner when load authentication */}
            {isLoad && (
            <div className="relative">
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-11 w-11 border-b-4 border-orange-500"></div>
              </div>
            </div>
            )}

            {/* Guest Items (Sign In / Sign Up) - Show when not authenticated */}
            {!isLoad && !isAuth && (
              <div className="hidden md:flex items-center space-x-2">
                {filteredGuestItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                      isActive(item.path)
                        ? "bg-orange-600 text-white"
                        : "text-white hover:bg-orange-600 hover:text-white border border-orange-600"
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}

            {/* Profile Dropdown - Show when authenticated */}
            {!isLoad && isAuth && (
              <div className="hidden md:flex relative">
                <button
                  onClick={toggleProfileMenu}
                  className="flex items-center space-x-0 p-0 rounded-full hover:bg-transparent focus:outline-none fill-black bg-black"
                >
                  {!avatarUrl ? <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 48 48" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M40.4554 41.4695C42.8406 39.2283 44.7408 36.5216 46.0385 33.5168C47.3362 30.512 48.0038 27.273 48 24C48 10.7446 37.2554 0 24 0C10.7446 0 1.60227e-05 10.7446 1.60227e-05 24C-0.00377106 27.273 0.663811 30.512 1.96149 33.5168C3.25917 36.5216 5.15937 39.2283 7.54463 41.4695C11.9925 45.6711 17.8815 48.0082 24 48C30.1185 48.0082 36.0075 45.6711 40.4554 41.4695ZM9.5877 38.3064C11.3159 36.1443 13.509 34.3993 16.004 33.201C18.4991 32.0027 21.2321 31.3819 24 31.3846C26.7679 31.3819 29.5009 32.0027 31.996 33.201C34.491 34.3993 36.6841 36.1443 38.4123 38.3064C36.5268 40.2109 34.282 41.7221 31.8079 42.7523C29.3339 43.7825 26.6799 44.3111 24 44.3077C21.3201 44.3111 18.6661 43.7825 16.1921 42.7523C13.718 41.7221 11.4732 40.2109 9.5877 38.3064ZM33.2308 16.6154C33.2308 19.0635 32.2582 21.4114 30.5271 23.1425C28.796 24.8736 26.4482 25.8461 24 25.8461C21.5519 25.8461 19.204 24.8736 17.4729 23.1425C15.7418 21.4114 14.7692 19.0635 14.7692 16.6154C14.7692 14.1672 15.7418 11.8193 17.4729 10.0882C19.204 8.35714 21.5519 7.38461 24 7.38461C26.4482 7.38461 28.796 8.35714 30.5271 10.0882C32.2582 11.8193 33.2308 14.1672 33.2308 16.6154Z" fill="white"/>
                  </svg> :
                  <img
                    src={avatarUrl}
                    alt={`${user?.first_name} ${user?.last_name}`}
                    className="size-10 rounded-full ring-2 ring-white"
                    onError={(e) => setAvatarUrl(null)}
                  />}
                </button>

                {/* Profile Dropdown Menu */}
                {isProfileOpen && (
                  <div className="absolute right-0 mt-10 w-48 bg-gray-950/30 rounded-lg shadow-lg border border-gray-950 text-white py-1 z-50">
                    {filteredProfileItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          target={item.external ? '_blank' : '_self'}
                          className="block px-4 py-2 text-sm hover:bg-gray-100 text-white "
                          onClick={() => handleMenuItemClick(item)}
                          role={item.action === 'logout' ? 'button' : 'link'}
                        >
                          {item.label}
                        </Link>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={toggleMenu}
                className="inline-flex items-center justify-center p-2 rounded-md bg-slate-900 text-gray-400 hover:text-gray-300 hover:bg-slate-600 focus:outline-none transition-colors"
              >
                {isOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {/* Mobile Search */}
            {/* {isAuth && (
              <div className="px-3 py-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                  />
                </div>
              </div>
            )} */}

            {/* Mobile menu items */}
            {filteredMenuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                target={item.external ? '_blank' : '_self'}
                className={`block px-3 py-2 text-base font-medium rounded-lg ${
                  isActive(item.path)
                    ? "text-orange-600 bg-orange-50"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
                onClick={() => handleMenuItemClick(item)}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Mobile Guest Links - Show when not authenticated */}
            {!isLoad && !isAuth && (
              <div className="border-t border-gray-200 pt-4">
                {filteredGuestItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`block px-3 py-2 text-base font-medium text-center rounded-lg ${
                      isActive(item.path)
                        ? "bg-orange-600 text-white"
                        : "text-orange-600 border border-orange-600 hover:bg-orange-600 hover:text-white"
                    }`}
                    onClick={() => handleMenuItemClick(item)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
            
            {/* Mobile Profile Links - Show when authenticated */}
            {!isLoad && isAuth && (
              <div className="border-t border-gray-200 pt-4">
                {filteredProfileItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          target={item.external ? '_blank' : '_self'}
                          className="block px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
                          onClick={() => handleMenuItemClick(item)}
                          role={item.action === 'logout' ? 'button' : 'link'}
                        >
                          {item.label}
                        </Link>
                      ))}
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;